import {
    HierarcicalObjectReference,
    ObjectData,
    ObjectId,
    SearchOptions,
    SearchPattern,
} from "@novorender/data-js-api";
import { ReadonlyVec3 } from "gl-matrix";

import { getProcoreObjectMetadata } from "./bimObjectMetadata";
import { procoreClient } from "./client";
import { and, contains, defined, equal, inPredicate, not, or, range, search, SearchPredicate } from "./searchPredicate";

/**
 * BIM Search DB API search request body
 * Matches the SearchRequest schema from the API
 */
interface BimSearchRequest {
    path?: string;
    descendants?: number | null;
    search?: SearchPredicate;
    continuation?: string;
    full?: boolean;
}

/**
 * BIM Search DB API search result (one line of the response)
 */
interface BimSearchResult {
    id: string;
    path: string;
    type: number;
    bounds?: {
        sphere: {
            center: ReadonlyVec3;
            radius: number;
        };
        box: {
            min: ReadonlyVec3;
            max: ReadonlyVec3;
        };
    };
    // Full metadata (only present if full: true)
    name?: string;
    properties?: [string, string][];
}

/**
 * Convert Novorender SearchPattern to Procore SearchPredicate.
 *
 * Implements conversion logic with backend compatibility:
 * - No property + value → Search (full text) predicate
 * - Range patterns → Range predicate
 * - Value patterns with exact=true → Equal predicate (or In if multiple values)
 * - Value patterns with exact=false/undefined → Contains predicate (or OR of Contains if multiple values)
 * - Property-only patterns → Defined predicate
 * - Exclude flag → sets negated: true on the predicate
 * - Multiple patterns → combined with And
 *
 * @param pattern - Novorender search pattern
 * @returns SearchPredicate
 */
function convertSearchPatternToPredicate(pattern: SearchPattern): SearchPredicate {
    const { property, value, exact, exclude, range: rangeValue } = pattern;

    let predicate: SearchPredicate;

    // No property → full text search
    if (!property) {
        if (value !== undefined) {
            const values = Array.isArray(value) ? value.map(String) : [String(value)];
            predicate = or(...values.map((v) => search(v)));
            return exclude ? not(predicate) : predicate;
        }
        throw new Error("Pattern must have a property name or a search value");
    }

    // Range pattern takes precedence
    if (rangeValue) {
        // Parse range values - try as numbers first, fall back to strings
        const minNum = parseFloat(rangeValue.min);
        const maxNum = parseFloat(rangeValue.max);

        if (!isNaN(minNum) && !isNaN(maxNum)) {
            predicate = range(property, minNum, maxNum);
        } else {
            predicate = range(property, rangeValue.min, rangeValue.max);
        }
    }
    // Value pattern
    else if (value !== undefined) {
        if (Array.isArray(value)) {
            if (exact) {
                predicate = inPredicate(property, value);
            } else {
                // Multiple non-exact values → OR of Contains predicates
                predicate = or(...value.map((v) => contains(property, String(v))));
            }
        } else {
            if (exact) {
                predicate = equal(property, value);
            } else {
                predicate = contains(property, String(value));
            }
        }
    }
    // Property-only pattern → check if property is defined
    else {
        predicate = defined(property);
    }

    return exclude ? not(predicate) : predicate;
}

/**
 * Convert array of SearchPatterns to a single SearchPredicate.
 * Multiple patterns are combined with And logic.
 *
 * @param patterns - Array of Novorender search patterns
 * @returns SearchPredicate (And predicate if multiple patterns, single predicate if one, undefined if empty)
 */
function convertSearchPatternsToPredicate(patterns: SearchPattern[]): SearchPredicate | undefined {
    if (patterns.length === 0) {
        return undefined;
    }

    const predicates = patterns.map(convertSearchPatternToPredicate);
    return and(...predicates);
}

/**
 * Search for objects in Procore BIM Search DB API
 *
 * Implements async generator to stream results with pagination support.
 * The BIM Search DB API returns newline-delimited text format:
 * - Line 1: continuation token for next page (empty if no more results)
 * - Lines 2+: JSON objects, one per line
 *
 * @param params - Request parameters including scene context and search filter
 * @returns Async iterator of HierarcicalObjectReference
 */
export async function* searchProcoreObjects(params: {
    companyId: number;
    projectId: number;
    sceneId: string;
    sceneVersionId: string;
    filter: SearchOptions;
    signal?: AbortSignal;
}): AsyncIterableIterator<HierarcicalObjectReference> {
    let continuationToken: string | undefined;

    // Convert SearchOptions to BIM Search DB request format
    const searchRequest = transformSearchOptions(params.filter);

    while (!params.signal?.aborted) {
        // Add continuation token for pagination
        if (continuationToken) {
            searchRequest.continuation = continuationToken;
        }

        // Make API request using procoreClient
        // Note: Using custom parseResponse to get raw text instead of default JSON parsing
        // The BIM Search DB API returns newline-delimited text format
        const response = await procoreClient<string>(
            `/rest/v2.0/companies/${params.companyId}/projects/${params.projectId}/bim/scenes/${params.sceneId}/versions/${params.sceneVersionId}/search`,
            {
                method: "POST",
                body: searchRequest,
                headers: {
                    Accept: "text/plain",
                },
                // Custom response parser for text/plain format
                parseResponse: async (res) => res.text(),
                // Pass abort signal to cancel HTTP request if search is aborted
                signal: params.signal,
            },
        );

        // Check abort signal after HTTP call completes
        if (params.signal?.aborted) {
            break;
        }

        // Empty response means no results found - this is valid
        if (!response || !response.trim()) {
            // No continuation token, and no results
            break;
        }

        // Parse newline-delimited response
        const lines = response.split("\n");
        continuationToken = lines[0]?.trim();

        // Process result lines (skip first line which is continuation token)
        const results = lines
            .slice(1)
            .filter((line: string) => line.trim())
            .map((line: string) => {
                try {
                    return JSON.parse(line) as BimSearchResult;
                } catch (e) {
                    console.warn("Failed to parse search result line:", line, e);
                    return null;
                }
            })
            .filter((result: BimSearchResult | null): result is BimSearchResult => result !== null);

        // Yield transformed results
        for (const result of results) {
            // Check abort signal before yielding each result
            if (params.signal?.aborted) {
                break;
            }
            yield transformToHierarchicalObjectReference(result, params);
        }

        // Stop if no more results
        if (!continuationToken) {
            break;
        }
    }
}

/**
 * Transform SearchOptions (Novorender SDK format) to BIM Search DB request format
 */
function transformSearchOptions(options: SearchOptions): BimSearchRequest {
    const request: BimSearchRequest = {};

    // Parent path filter
    if (options.parentPath !== undefined) {
        request.path = options.parentPath;
    }

    // Include relatives of matched objects in results:
    // - Positive values: include N levels of children (descendants)
    // - Negative values: include all parents up the hierarchy
    // - 0 or undefined: return only the matched object itself
    if (options.descentDepth !== undefined) {
        request.descendants = options.descentDepth;
    }

    // Full metadata preload
    if (options.full !== undefined) {
        request.full = options.full;
    }

    // Search patterns - convert to predicate format
    if (options.searchPattern) {
        const patterns = Array.isArray(options.searchPattern) ? options.searchPattern : [options.searchPattern];

        // Filter out string patterns (not supported by SearchPattern type)
        const validPatterns = patterns.filter((p): p is SearchPattern => typeof p !== "string");

        // Convert to predicate format
        if (validPatterns.length > 0) {
            const predicate = convertSearchPatternsToPredicate(validPatterns);
            if (predicate) {
                request.search = predicate;
            }
        }
    }

    return request;
}

/**
 * Transform BIM Search DB result to HierarcicalObjectReference format
 */
function transformToHierarchicalObjectReference(
    result: BimSearchResult,
    params: {
        companyId: number;
        projectId: number;
        sceneId: string;
        sceneVersionId: string;
    },
): HierarcicalObjectReference {
    // Parse ID - API returns string, we need number
    const id = parseInt(result.id, 10);

    const bounds = result.bounds
        ? {
              sphere: {
                  center: result.bounds.sphere.center,
                  radius: result.bounds.sphere.radius,
              },
              box: {
                  min: result.bounds.box.min,
                  max: result.bounds.box.max,
              },
          }
        : undefined;

    // If full metadata was requested and returned, return ObjectData directly
    if (result.name && result.properties) {
        const objectData: ObjectData = {
            id: id as ObjectId,
            name: result.name,
            path: result.path,
            type: result.type,
            properties: result.properties,
            bounds,
            async loadMetaData(): Promise<ObjectData> {
                return objectData;
            },
            async save() {
                return false;
            },
        };
        return objectData;
    }

    // Otherwise, return HierarcicalObjectReference with lazy loading
    const ref: HierarcicalObjectReference = {
        id: id as ObjectId,
        path: result.path,
        type: result.type,
        bounds,
        async loadMetaData(): Promise<ObjectData> {
            return getProcoreObjectMetadata({
                ...params,
                objectId: id,
            });
        },
    };

    return ref;
}
